import { Box, Typography } from '@mui/material';
import SectionFormBox from '../../../components/SectionFormBox/SectionFormBox';
import { useTranslation } from 'react-i18next';

interface OccurrenceLocationLocalidad {
    nombre?: string;
    codigoPostal?: string | number;
    provincia?: {
        nombre?: string;
    };
}

export interface OccurrenceLocation {
    calle?: string;
    altura?: string;
    piso?: string | null;
    depto?: string | null;
    codigoPostal?: string | number | null;
    localidad?: string;
    provincia?: string;
    localidadData?: OccurrenceLocationLocalidad;
}

interface Props {
    occurrenceLocation?: OccurrenceLocation | null;
    claimNumber?: string | number | null;
    variant?: 'card' | 'tooltip';
}

export const getOccurrenceLocationDisplayData = (
    occurrenceLocation?: OccurrenceLocation | null,
    claimNumber?: string | number | null
) => {
    if (!occurrenceLocation) return null;

    const streetLine = [occurrenceLocation.calle, occurrenceLocation.altura]
        .filter(Boolean)
        .join(' ')
        .trim();
    const floorLine = [
        occurrenceLocation.piso ? `Piso ${occurrenceLocation.piso}` : '',
        occurrenceLocation.depto ? `Depto ${occurrenceLocation.depto}` : ''
    ]
        .filter(Boolean)
        .join(' ')
        .trim();

    const localityName = occurrenceLocation.localidad || occurrenceLocation.localidadData?.nombre;
    const provinceName =
        occurrenceLocation.provincia || occurrenceLocation.localidadData?.provincia?.nombre;
    const postalCode =
        occurrenceLocation.codigoPostal || occurrenceLocation.localidadData?.codigoPostal;

    const hasUsefulData = Boolean(
        occurrenceLocation.calle ||
        occurrenceLocation.altura ||
        occurrenceLocation.piso ||
        occurrenceLocation.depto ||
        postalCode ||
        localityName ||
        provinceName
    );

    if (!hasUsefulData) return null;

    return {
        claimNumber: claimNumber || 'N/A',
        address: [streetLine, floorLine].filter(Boolean).join(' - ') || 'N/A',
        locality: localityName || 'N/A',
        province: provinceName || 'N/A',
        postalCode: postalCode || 'N/A'
    };
};

const LugarOcurrenciaSeleccionado = ({
    occurrenceLocation,
    claimNumber,
    variant = 'card'
}: Props) => {
    const { t } = useTranslation('visitas');
    const displayData = getOccurrenceLocationDisplayData(occurrenceLocation, claimNumber);
    const showInlineTitle = variant === 'tooltip';

    if (!displayData) {
        return null;
    }

    const content = (
        <Box sx={{ width: '100%', padding: variant === 'card' ? '10px' : 0 }}>
            <Box
                sx={{
                    p: variant === 'card' ? 0.5 : 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    minWidth: variant === 'tooltip' ? 320 : 'auto'
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '0.75fr auto',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: variant === 'tooltip' ? '#FFFFFF' : 'text.secondary',
                            fontWeight: 700,
                            visibility: showInlineTitle ? 'visible' : 'hidden'
                        }}
                    >
                        {t('newVisit.selectedOccurrenceLocation')}
                    </Typography>
                    <Box
                        sx={{
                            px: 1,
                            py: 0.375,
                            borderRadius: 999,
                            bgcolor: 'primary.main',
                            color: '#FFFFFF',
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            width: 'fit-content'
                        }}
                    >
                        {`Nro ROAM: ${displayData.claimNumber}`}
                    </Box>
                </Box>

                <Typography
                    variant="body2"
                    sx={{ color: variant === 'tooltip' ? '#FFFFFF' : 'text.secondary' }}
                >
                    {displayData.address}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '0.5fr auto', gap: 2 }}>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                color:
                                    variant === 'tooltip'
                                        ? 'rgba(255,255,255,0.72)'
                                        : 'text.secondary',
                                fontWeight: 600,
                                textTransform: 'uppercase'
                            }}
                        >
                            {t('filters.province')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: variant === 'tooltip' ? '#FFFFFF' : 'text.secondary' }}
                        >
                            {displayData.province}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                color:
                                    variant === 'tooltip'
                                        ? 'rgba(255,255,255,0.72)'
                                        : 'text.secondary',
                                fontWeight: 600,
                                textTransform: 'uppercase'
                            }}
                        >
                            {t('filters.city')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: variant === 'tooltip' ? '#FFFFFF' : 'text.secondary' }}
                        >
                            {displayData.locality}
                            <br />({t('filters.postalCode')}: {displayData.postalCode})
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    if (variant === 'tooltip') {
        return content;
    }

    return (
        <SectionFormBox
            title={
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    {t('newVisit.selectedOccurrenceLocation')}
                </Typography>
            }
        >
            {content}
        </SectionFormBox>
    );
};

export default LugarOcurrenciaSeleccionado;
